import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthToken } from "@/hooks/useAuthToken";
import { VITE_BACKEND_URL } from "../../../api/config";
import "./GestionBeneficiarios.css";

interface Beneficiario {
  beneficiario_id: number;
  tipo: string;
  nombre: string;
  documento_identidad?: string | null;
  direccion?: string | null;
  calle?: string | null;
  numero?: string | null;
  bloque?: string | null;
  escalera?: string | null;
  piso?: string | null;
  puerta?: string | null;
  complemento_direccion?: string | null;
  codigo_postal?: string | null;
  localidad?: string | null;
  provincia?: string | null;
  persona_contacto?: string | null;
  telefono?: string | null;
  email?: string | null;
  observaciones_privadas?: string | null;
  nombre_publico?: string | null;
  descripcion_publica?: string | null;
  localidad_publica?: string | null;
  autorizacion_publicacion: number | boolean;
  activo: number | boolean;
}

interface BeneficiarioDraft {
  tipo: string;
  nombre: string;
  documento_identidad: string;
  calle: string;
  numero: string;
  bloque: string;
  escalera: string;
  piso: string;
  puerta: string;
  complemento_direccion: string;
  codigo_postal: string;
  localidad: string;
  provincia: string;
  persona_contacto: string;
  telefono: string;
  email: string;
  observaciones_privadas: string;
  nombre_publico: string;
  descripcion_publica: string;
  localidad_publica: string;
  autorizacion_publicacion: boolean;
}

interface GestionBeneficiariosProps {
  onBeneficiariosChanged?: () => Promise<void> | void;
}

const API_URL = VITE_BACKEND_URL?.replace(/\/+$/, "");

const emptyDraft = (): BeneficiarioDraft => ({
  tipo: "",
  nombre: "",
  documento_identidad: "",
  calle: "",
  numero: "",
  bloque: "",
  escalera: "",
  piso: "",
  puerta: "",
  complemento_direccion: "",
  codigo_postal: "",
  localidad: "",
  provincia: "",
  persona_contacto: "",
  telefono: "",
  email: "",
  observaciones_privadas: "",
  nombre_publico: "",
  descripcion_publica: "",
  localidad_publica: "",
  autorizacion_publicacion: false,
});

const isEnabled = (value: number | boolean | null | undefined) =>
  value === true || value === 1;

const getErrorMessage = (data: unknown, fallback: string) => {
  if (data && typeof data === "object") {
    const record = data as Record<string, unknown>;
    if (typeof record.message === "string") return record.message;
    if (typeof record.error === "string") return record.error;
  }
  return fallback;
};

const toDraft = (item: Beneficiario): BeneficiarioDraft => ({
  tipo: item.tipo ?? "",
  nombre: item.nombre ?? "",
  documento_identidad: item.documento_identidad ?? "",
  calle: item.calle || item.direccion || "",
  numero: item.numero ?? "",
  bloque: item.bloque ?? "",
  escalera: item.escalera ?? "",
  piso: item.piso ?? "",
  puerta: item.puerta ?? "",
  complemento_direccion: item.complemento_direccion ?? "",
  codigo_postal: item.codigo_postal ?? "",
  localidad: item.localidad ?? "",
  provincia: item.provincia ?? "",
  persona_contacto: item.persona_contacto ?? "",
  telefono: item.telefono ?? "",
  email: item.email ?? "",
  observaciones_privadas: item.observaciones_privadas ?? "",
  nombre_publico: item.nombre_publico ?? "",
  descripcion_publica: item.descripcion_publica ?? "",
  localidad_publica: item.localidad_publica ?? "",
  autorizacion_publicacion: isEnabled(item.autorizacion_publicacion),
});

const GestionBeneficiarios: React.FC<GestionBeneficiariosProps> = ({
  onBeneficiariosChanged,
}) => {
  const { token } = useAuthToken();
  const [beneficiarios, setBeneficiarios] = useState<Beneficiario[]>([]);
  const [draft, setDraft] = useState<BeneficiarioDraft>(emptyDraft);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingId, setChangingId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [message, setMessage] = useState<{
    text: string;
    type: "success" | "error" | "warning";
  } | null>(null);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, Accept: "application/json" }),
    [token],
  );

  const loadBeneficiarios = useCallback(async () => {
    if (!API_URL || !token) {
      setBeneficiarios([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/api/superadmin/donaciones/beneficiarios`,
        {
          headers,
          cache: "no-store",
        },
      );
      const data: unknown = await response.json();
      if (!response.ok) {
        throw new Error(
          getErrorMessage(data, "No se pudieron cargar los beneficiarios."),
        );
      }
      if (!Array.isArray(data))
        throw new Error("La respuesta de beneficiarios no es válida.");
      setBeneficiarios(data as Beneficiario[]);
    } catch (error) {
      setMessage({
        text:
          error instanceof Error
            ? error.message
            : "Error al cargar los beneficiarios.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [headers, token]);

  useEffect(() => {
    void loadBeneficiarios();
  }, [loadBeneficiarios]);

  const filteredBeneficiarios = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase("es");
    return beneficiarios.filter((item) => {
      if (!showInactive && !isEnabled(item.activo)) return false;
      if (!normalizedSearch) return true;
      return [
        item.nombre,
        item.tipo,
        item.documento_identidad,
        item.localidad,
        item.provincia,
      ].some((value) =>
        value?.toLocaleLowerCase("es").includes(normalizedSearch),
      );
    });
  }, [beneficiarios, search, showInactive]);

  const changeField = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = event.target;
    setDraft((previous) => ({ ...previous, [name]: value }));
  };

  const resetForm = () => {
    setDraft(emptyDraft());
    setEditingId(null);
  };

  const editBeneficiario = (item: Beneficiario) => {
    setDraft(toDraft(item));
    setEditingId(item.beneficiario_id);
    setMessage(null);
    document
      .getElementById("beneficiario-form-title")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submitBeneficiario = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (!API_URL || !token) {
      setMessage({
        text: "No está configurada la conexión con el servidor.",
        type: "error",
      });
      return;
    }
    if (!draft.tipo.trim() || !draft.nombre.trim()) {
      setMessage({
        text: "El tipo y el nombre del beneficiario son obligatorios.",
        type: "warning",
      });
      return;
    }
    if (draft.autorizacion_publicacion && !draft.nombre_publico.trim()) {
      setMessage({
        text: "Si autorizas la publicación, debes indicar un nombre público.",
        type: "warning",
      });
      return;
    }

    const direccion = [
      [draft.calle.trim(), draft.numero.trim()].filter(Boolean).join(" "),
      draft.bloque.trim() && `Bloque ${draft.bloque.trim()}`,
      draft.escalera.trim() && `Esc. ${draft.escalera.trim()}`,
      draft.piso.trim() && `Piso ${draft.piso.trim()}`,
      draft.puerta.trim() && `Puerta ${draft.puerta.trim()}`,
      draft.complemento_direccion.trim(),
    ]
      .filter(Boolean)
      .join(", ");

    const payload = {
      ...draft,
      tipo: draft.tipo.trim(),
      nombre: draft.nombre.trim(),
      documento_identidad: draft.documento_identidad.trim() || null,
      calle: draft.calle.trim() || null,
      numero: draft.numero.trim() || null,
      bloque: draft.bloque.trim() || null,
      escalera: draft.escalera.trim() || null,
      piso: draft.piso.trim() || null,
      puerta: draft.puerta.trim() || null,
      complemento_direccion: draft.complemento_direccion.trim() || null,
      direccion: direccion || null,
      codigo_postal: draft.codigo_postal.trim() || null,
      localidad: draft.localidad.trim() || null,
      provincia: draft.provincia.trim() || null,
      persona_contacto: draft.persona_contacto.trim() || null,
      telefono: draft.telefono.trim() || null,
      email: draft.email.trim() || null,
      observaciones_privadas: draft.observaciones_privadas.trim() || null,
      nombre_publico: draft.nombre_publico.trim() || null,
      descripcion_publica: draft.descripcion_publica.trim() || null,
      localidad_publica: draft.localidad_publica.trim() || null,
      autorizacion_publicacion: draft.autorizacion_publicacion,
    };

    try {
      setSaving(true);
      const response = await fetch(
        editingId
          ? `${API_URL}/api/superadmin/donaciones/beneficiarios/${editingId}`
          : `${API_URL}/api/superadmin/donaciones/beneficiarios`,
        {
          method: editingId ? "PUT" : "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      const data: unknown = await response.json();
      if (!response.ok) {
        throw new Error(
          getErrorMessage(
            data,
            "No se pudo guardar la ficha del beneficiario.",
          ),
        );
      }
      await loadBeneficiarios();
      await onBeneficiariosChanged?.();
      setMessage({
        text: editingId
          ? "Ficha del beneficiario actualizada."
          : "Beneficiario creado correctamente.",
        type: "success",
      });
      resetForm();
    } catch (error) {
      setMessage({
        text:
          error instanceof Error
            ? error.message
            : "Error al guardar el beneficiario.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item: Beneficiario) => {
    if (!API_URL || !token) return;
    const nextActive = !isEnabled(item.activo);
    try {
      setChangingId(item.beneficiario_id);
      const response = await fetch(
        `${API_URL}/api/superadmin/donaciones/beneficiarios/${item.beneficiario_id}/estado`,
        {
          method: "PATCH",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ activo: nextActive }),
        },
      );
      const data: unknown = await response.json();
      if (!response.ok)
        throw new Error(
          getErrorMessage(
            data,
            "No se pudo cambiar el estado del beneficiario.",
          ),
        );
      await loadBeneficiarios();
      await onBeneficiariosChanged?.();
      setMessage({
        text: nextActive
          ? "Beneficiario activado."
          : "Beneficiario desactivado.",
        type: "success",
      });
    } catch (error) {
      setMessage({
        text:
          error instanceof Error
            ? error.message
            : "Error al cambiar el estado.",
        type: "error",
      });
    } finally {
      setChangingId(null);
    }
  };

  return (
    <section className="donaciones-card gestion-beneficiarios">
      <div className="donaciones-card-heading gestion-beneficiarios__heading">
        <div>
          <h2>Fichas de beneficiarios</h2>
          <p>
            Registra y administra los datos de las personas o entidades que
            reciben las ayudas.
          </p>
        </div>
        <span className="gestion-beneficiarios__count">
          {beneficiarios.length} fichas
        </span>
      </div>

      {message && (
        <div
          className={`gestion-beneficiarios__message gestion-beneficiarios__message--${message.type}`}
          role="status"
        >
          <span>{message.text}</span>
          <button
            type="button"
            onClick={() => setMessage(null)}
            aria-label="Cerrar aviso"
          >
            ×
          </button>
        </div>
      )}

      <form
        className="gestion-beneficiarios__form"
        onSubmit={submitBeneficiario}
      >
        <div className="gestion-beneficiarios__form-heading">
          <div>
            <span className="gestion-beneficiarios__eyebrow">
              FICHA INTERNA
            </span>
            <h3 id="beneficiario-form-title">
              {editingId ? "Editar beneficiario" : "Nuevo beneficiario"}
            </h3>
          </div>
          {editingId && (
            <button
              type="button"
              className="gestion-beneficiarios__secondary"
              onClick={resetForm}
            >
              Cancelar edición
            </button>
          )}
        </div>

        <div className="gestion-beneficiarios__group">
          <h4>Identificación</h4>
          <div className="gestion-beneficiarios__grid">
            <label>
              Tipo de beneficiario *
              <input
                name="tipo"
                value={draft.tipo}
                onChange={changeField}
                placeholder="Estudiante, familia, ONG, fundación..."
                maxLength={40}
                required
              />
            </label>
            <label>
              Nombre real o razón social *
              <input
                name="nombre"
                value={draft.nombre}
                onChange={changeField}
                maxLength={255}
                required
              />
            </label>
            <label>
              DNI / NIE / NIF / CIF
              <input
                name="documento_identidad"
                value={draft.documento_identidad}
                onChange={changeField}
                maxLength={30}
                autoComplete="off"
              />
            </label>
          </div>
        </div>

        <div className="gestion-beneficiarios__group">
          <h4>Domicilio privado</h4>
          <div className="gestion-beneficiarios__grid gestion-beneficiarios__grid--address">
            <label className="gestion-beneficiarios__wide">
              Calle
              <input
                name="calle"
                value={draft.calle}
                onChange={changeField}
                maxLength={180}
                autoComplete="street-address"
              />
            </label>
            <label>
              Número
              <input
                name="numero"
                value={draft.numero}
                onChange={changeField}
                maxLength={20}
              />
            </label>
            <label>
              Bloque
              <input
                name="bloque"
                value={draft.bloque}
                onChange={changeField}
                maxLength={30}
              />
            </label>
            <label>
              Escalera
              <input
                name="escalera"
                value={draft.escalera}
                onChange={changeField}
                maxLength={30}
              />
            </label>
            <label>
              Piso
              <input
                name="piso"
                value={draft.piso}
                onChange={changeField}
                maxLength={20}
              />
            </label>
            <label>
              Puerta
              <input
                name="puerta"
                value={draft.puerta}
                onChange={changeField}
                maxLength={20}
              />
            </label>
            <label className="gestion-beneficiarios__wide">
              Otros datos del domicilio
              <input
                name="complemento_direccion"
                value={draft.complemento_direccion}
                onChange={changeField}
                maxLength={180}
                placeholder="Urbanización, escalera adicional, referencia..."
              />
            </label>
            <label>
              Código postal
              <input
                name="codigo_postal"
                value={draft.codigo_postal}
                onChange={changeField}
                maxLength={20}
                autoComplete="postal-code"
              />
            </label>
            <label>
              Localidad
              <input
                name="localidad"
                value={draft.localidad}
                onChange={changeField}
                maxLength={120}
                autoComplete="address-level2"
              />
            </label>
            <label>
              Provincia
              <input
                name="provincia"
                value={draft.provincia}
                onChange={changeField}
                maxLength={120}
                autoComplete="address-level1"
              />
            </label>
          </div>
        </div>

        <div className="gestion-beneficiarios__group">
          <h4>Contacto privado</h4>
          <div className="gestion-beneficiarios__grid">
            <label>
              Persona de contacto
              <input
                name="persona_contacto"
                value={draft.persona_contacto}
                onChange={changeField}
                maxLength={255}
              />
            </label>
            <label>
              Teléfono
              <input
                name="telefono"
                value={draft.telefono}
                onChange={changeField}
                maxLength={50}
                autoComplete="tel"
              />
            </label>
            <label>
              Email
              <input
                type="email"
                name="email"
                value={draft.email}
                onChange={changeField}
                maxLength={255}
                autoComplete="email"
              />
            </label>
          </div>
          <label className="gestion-beneficiarios__private-notes">
            Observaciones privadas
            <textarea
              name="observaciones_privadas"
              value={draft.observaciones_privadas}
              onChange={changeField}
              rows={4}
              placeholder="Información interna. No se mostrará públicamente."
            />
          </label>
        </div>

        <div className="gestion-beneficiarios__group gestion-beneficiarios__public-group">
          <div className="gestion-beneficiarios__public-heading">
            <div>
              <h4>Información pública</h4>
              <p>
                Solo se utilizará en el histórico cuando exista autorización
                expresa y se active la publicación de la donación.
              </p>
            </div>
            <label className="gestion-beneficiarios__authorization">
              <input
                type="checkbox"
                checked={draft.autorizacion_publicacion}
                onChange={(event) =>
                  setDraft((previous) => ({
                    ...previous,
                    autorizacion_publicacion: event.target.checked,
                  }))
                }
              />{" "}
              Autorización expresa para publicar
            </label>
          </div>
          <div className="gestion-beneficiarios__grid">
            <label>
              Nombre público
              <input
                name="nombre_publico"
                value={draft.nombre_publico}
                onChange={changeField}
                maxLength={255}
                disabled={!draft.autorizacion_publicacion}
              />
            </label>
            <label>
              Localidad pública
              <input
                name="localidad_publica"
                value={draft.localidad_publica}
                onChange={changeField}
                maxLength={120}
                disabled={!draft.autorizacion_publicacion}
              />
            </label>
            <label className="gestion-beneficiarios__wide">
              Descripción pública
              <textarea
                name="descripcion_publica"
                value={draft.descripcion_publica}
                onChange={changeField}
                rows={3}
                maxLength={500}
                disabled={!draft.autorizacion_publicacion}
              />
            </label>
          </div>
          {!draft.autorizacion_publicacion && (
            <p className="gestion-beneficiarios__privacy-note">
              La ficha permanecerá privada. No se publicará el nombre real ni
              ningún dato de contacto o domicilio.
            </p>
          )}
        </div>

        <div className="gestion-beneficiarios__actions">
          <button type="submit" className="donaciones-submit" disabled={saving}>
            {saving
              ? "Guardando ficha..."
              : editingId
                ? "Guardar cambios"
                : "Crear beneficiario"}
          </button>
          {!editingId && (
            <button
              type="button"
              className="gestion-beneficiarios__secondary"
              onClick={resetForm}
            >
              Limpiar formulario
            </button>
          )}
        </div>
      </form>

      <div className="gestion-beneficiarios__list-heading">
        <div>
          <h3>Beneficiarios registrados</h3>
          <p>
            Los beneficiarios inactivos se conservan y no se pueden seleccionar
            para nuevas donaciones.
          </p>
        </div>
        <label className="gestion-beneficiarios__inactive-filter">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(event) => setShowInactive(event.target.checked)}
          />{" "}
          Mostrar inactivos
        </label>
      </div>
      <input
        className="gestion-beneficiarios__search"
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Buscar por nombre, tipo, documento o localidad..."
        aria-label="Buscar beneficiarios"
      />

      {loading ? (
        <p className="donaciones-empty">Cargando beneficiarios...</p>
      ) : filteredBeneficiarios.length === 0 ? (
        <p className="donaciones-empty">
          No hay beneficiarios que coincidan con la búsqueda.
        </p>
      ) : (
        <div className="donaciones-table-wrapper">
          <table className="donaciones-table gestion-beneficiarios__table">
            <thead>
              <tr>
                <th>Beneficiario</th>
                <th>Documento</th>
                <th>Localidad</th>
                <th>Estado</th>
                <th>Publicación</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredBeneficiarios.map((item) => (
                <tr key={item.beneficiario_id}>
                  <td>
                    <strong>{item.nombre}</strong>
                    <small>{item.tipo}</small>
                  </td>
                  <td>{item.documento_identidad || "—"}</td>
                  <td>
                    {[item.localidad, item.provincia]
                      .filter(Boolean)
                      .join(", ") || "—"}
                  </td>
                  <td>
                    <span
                      className={`gestion-beneficiarios__status ${isEnabled(item.activo) ? "is-active" : "is-inactive"}`}
                    >
                      {isEnabled(item.activo) ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td>
                    {isEnabled(item.autorizacion_publicacion) &&
                    item.nombre_publico
                      ? "Autorizada"
                      : "Privada"}
                  </td>
                  <td>
                    <div className="gestion-beneficiarios__row-actions">
                      <button
                        type="button"
                        className="gestion-beneficiarios__edit"
                        onClick={() => editBeneficiario(item)}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="gestion-beneficiarios__toggle"
                        onClick={() => void toggleActive(item)}
                        disabled={changingId === item.beneficiario_id}
                      >
                        {changingId === item.beneficiario_id
                          ? "Guardando..."
                          : isEnabled(item.activo)
                            ? "Desactivar"
                            : "Activar"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default GestionBeneficiarios;
